import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  GripVertical,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import ApiClient from "../../lib/apiClient";
import { isDeveloperCaoScout } from "../../lib/developer-cao-scout";
import {
  createScoutService,
  type ScoutRecord,
  type ScoutRun,
} from "../../lib/services/scouts";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

interface DebriefCandidate {
  scout: ScoutRecord;
  run: ScoutRun;
}

const DISMISS_PREFIX = "fintheon:developer-cao-debrief-dismissed:";

export function DeveloperDebriefPopup() {
  const { getAccessToken } = useAuth();
  const [candidate, setCandidate] = useState<DebriefCandidate | null>(null);
  const [position, setPosition] = useState(() => defaultPosition());
  const [drag, setDrag] = useState<{
    id: number;
    x: number;
    y: number;
    left: number;
    top: number;
  } | null>(null);
  const service = useMemo(
    () => createScoutService(new ApiClient().withAuth(getAccessToken)),
    [getAccessToken],
  );

  const refresh = useCallback(async () => {
    try {
      const scouts = (await service.list()).filter(isDeveloperCaoScout);
      const results = await Promise.all(
        scouts.map(async (scout) => ({
          scout,
          runs: await service.listRuns(scout.id),
        })),
      );
      const next = results
        .flatMap(({ scout, runs }) =>
          runs.map((run) => ({ scout, run }) satisfies DebriefCandidate),
        )
        .filter(
          ({ run }) => run.status === "succeeded" || run.status === "failed",
        )
        .find(({ run }) => !isDismissed(run.id));
      setCandidate(next ?? null);
    } catch {
      setCandidate(null);
    }
  }, [service]);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => void refresh(), 60_000);
    window.addEventListener("fintheon:scout-created", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("fintheon:scout-created", refresh);
    };
  }, [refresh]);

  if (!candidate) return null;
  const isSuccess = candidate.run.status === "succeeded";
  const detail =
    candidate.run.output || candidate.run.error || "Debug run completed.";

  return (
    <div
      className="fixed z-[120] w-[380px] max-w-[calc(100vw-24px)] overflow-hidden rounded-lg border border-[#c79f4a]/18 bg-[#0a0905]/92 text-[#f0ead6] backdrop-blur-xl"
      style={{ left: position.left, top: position.top }}
      onPointerMove={(event) => {
        if (!drag || event.pointerId !== drag.id) return;
        setPosition({
          left: clamp(
            drag.left + event.clientX - drag.x,
            12,
            window.innerWidth - 392,
          ),
          top: clamp(
            drag.top + event.clientY - drag.y,
            12,
            window.innerHeight - 260,
          ),
        });
      }}
      onPointerUp={(event) => {
        if (drag?.id === event.pointerId) {
          event.currentTarget.releasePointerCapture(event.pointerId);
          setDrag(null);
        }
      }}
    >
      <header
        className="flex cursor-grab items-center justify-between border-b border-[#c79f4a]/12 px-3 py-2 active:cursor-grabbing"
        onPointerDown={(event) => {
          event.currentTarget.parentElement?.setPointerCapture(event.pointerId);
          setDrag({
            id: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            left: position.left,
            top: position.top,
          });
        }}
      >
        <div className="flex min-w-0 items-center gap-2">
          <GripVertical className="h-3.5 w-3.5 shrink-0 text-[#c79f4a]/45" />
          {isSuccess ? (
            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-300/72" />
          ) : (
            <AlertTriangle className="h-4 w-4 shrink-0 text-[#ac5318]" />
          )}
          <span className="truncate text-[10px] font-semibold uppercase tracking-[0.16em] text-[#c79f4a]">
            Developer Debrief
          </span>
        </div>
        <button
          type="button"
          className="grid h-6 w-6 place-items-center rounded-[4px] text-[#f0ead6]/42 hover:bg-[#c79f4a]/8 hover:text-[#f0ead6]/78"
          title="Dismiss debrief"
          onClick={() => {
            dismiss(candidate.run.id);
            setCandidate(null);
          }}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </header>
      <div className="px-3 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-[12px] font-semibold text-[#f0ead6]/86">
              {candidate.scout.name}
            </p>
            <p className="mt-0.5 text-[10px] uppercase tracking-[0.12em] text-[#f0ead6]/38">
              {candidate.run.status} ·{" "}
              <FintheonNumericText
                text={formatTime(candidate.run.completedAt)}
                kind="date-time"
              />
            </p>
          </div>
          <button
            type="button"
            className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-[4px] border border-[#c79f4a]/18 px-2 text-[10px] font-semibold text-[#c79f4a] hover:bg-[#c79f4a]/8"
            onClick={() =>
              window.dispatchEvent(
                new CustomEvent("fintheon:navigate-tab", {
                  detail: { tab: "scouts" },
                }),
              )
            }
          >
            <ExternalLink className="h-3 w-3" />
            Scouts
          </button>
        </div>
        <pre className="mt-3 max-h-52 overflow-y-auto whitespace-pre-wrap rounded-md border border-[#c79f4a]/10 bg-[#050402]/72 p-2.5 text-[11px] leading-relaxed text-[#f0ead6]/68">
          {detail}
        </pre>
      </div>
    </div>
  );
}

function defaultPosition() {
  if (typeof window === "undefined") return { left: 24, top: 120 };
  return {
    left: Math.max(12, window.innerWidth - 404),
    top: Math.max(12, window.innerHeight - 360),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function isDismissed(runId: string): boolean {
  try {
    return localStorage.getItem(`${DISMISS_PREFIX}${runId}`) === "true";
  } catch {
    return false;
  }
}

function dismiss(runId: string) {
  try {
    localStorage.setItem(`${DISMISS_PREFIX}${runId}`, "true");
  } catch {
    /* ignore */
  }
}

function formatTime(value: string | null): string {
  if (!value) return "pending";
  return new Date(value).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}
